<template>
  <article
    class="ai-msg ai-rise"
    :class="[`ai-msg--${message.role}`, { 'is-streaming': message.streaming, 'is-error': !!message.error }]"
    :data-testid="`ai-message-${message.role}`"
  >
    <span v-if="message.role === 'assistant'" aria-hidden="true" class="ai-orb ai-msg__avatar">
      <Sparkles :size="13" />
    </span>

    <div class="ai-msg__bubble">
      <!-- Nutzertext: reiner Text, Zeilenumbrüche bleiben erhalten. -->
      <p v-if="message.role === 'user'" class="ai-msg__user-text">{{ message.content }}</p>

      <template v-else>
        <p v-if="message.error" class="ai-msg__error">
          <AlertTriangle aria-hidden="true" :size="14" />
          <span>{{ message.error }}</span>
        </p>

        <div v-else class="ai-md">
          <template v-for="(block, b) in blocks" :key="b">
            <h4 v-if="block.kind === 'heading'" class="ai-md__h">
              <InlineRuns :runs="block.runs" @navigate="navigate" />
            </h4>
            <p v-else-if="block.kind === 'paragraph'" class="ai-md__p">
              <InlineRuns :runs="block.runs" @navigate="navigate" />
            </p>
            <component :is="block.ordered ? 'ol' : 'ul'" v-else class="ai-md__list" :class="{ 'is-ordered': block.ordered }">
              <li v-for="(item, i) in block.items" :key="i">
                <InlineRuns :runs="item" @navigate="navigate" />
              </li>
            </component>
          </template>
          <span v-if="message.streaming" aria-hidden="true" class="ai-md__caret" />
        </div>

        <footer v-if="!message.streaming && !message.error" class="ai-msg__footer">
          <div v-if="message.sources.length > 0" class="ai-msg__sources">
            <BookOpen aria-hidden="true" :size="12" />
            <button
              v-for="source in message.sources.slice(0, 3)"
              :key="source.id"
              class="ai-msg__source"
              :title="$gt('Im Hilfe-Center öffnen')"
              type="button"
              @click="openSource(source)"
            >
              {{ source.title }}
            </button>
          </div>
          <div v-if="message.id" class="ai-msg__rating">
            <button
              :aria-label="$gt('Hilfreich')"
              :aria-pressed="message.rating === 1"
              class="ai-icon-btn ai-icon-btn--dark ai-msg__rate"
              :class="{ 'is-on': message.rating === 1 }"
              type="button"
              @click="$emit('rate', message, 1)"
            >
              <ThumbsUp :size="14" />
            </button>
            <button
              :aria-label="$gt('Nicht hilfreich')"
              :aria-pressed="message.rating === -1"
              class="ai-icon-btn ai-icon-btn--dark ai-msg__rate"
              :class="{ 'is-on is-down': message.rating === -1 }"
              type="button"
              @click="$emit('rate', message, -1)"
            >
              <ThumbsDown :size="14" />
            </button>
          </div>
        </footer>
      </template>
    </div>
  </article>
</template>

<script setup lang="ts">
  import type { AssistantSource } from '@/services/assistantApi'
  import type { AssistantMessage } from '@/stores/assistantStore'
  import { AlertTriangle, ArrowUpRight, BookOpen, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-vue-next'
  import { computed, defineComponent, h, type PropType, toRef } from 'vue'
  import { type RouteLocationRaw, useRouter } from 'vue-router'
  import { resolveRouteToken } from './assistantLinks'
  import { type InlineRun, parseBlocks, streamingSafeText } from './assistantMarkdown'
  import { useTypewriter } from './useTypewriter'

  const props = defineProps<{ message: AssistantMessage }>()

  const emit = defineEmits<{
    (e: 'rate', message: AssistantMessage, rating: 1 | -1): void
    (e: 'navigate', to: RouteLocationRaw): void
  }>()

  const router = useRouter()
  const catalog = {
    hasRoute: (name: string) => router.hasRoute(name),
    pathOf: (name: string) => router.getRoutes().find(r => r.name === name)?.path,
  }

  const content = toRef(props.message, 'content')
  const streaming = toRef(props.message, 'streaming')
  const { displayed } = useTypewriter(content, streaming)

  const blocks = computed(() => parseBlocks(props.message.streaming ? streamingSafeText(displayed.value) : props.message.content))

  function navigate (to: RouteLocationRaw) {
    emit('navigate', to)
  }

  function openSource (source: AssistantSource) {
    const to: RouteLocationRaw = source.route
      ? { name: source.route.name, query: source.route.query, params: source.route.params }
      : { name: 'PageHelp', query: { artikel: source.id } }
    emit('navigate', to)
  }

  /**
   * Rendert Inline-Läufe als Text, <strong>, <code> oder Link-Pille. Als
   * Render-Funktion, damit ausschließlich Textknoten entstehen (kein HTML
   * aus Modellantworten).
   */
  const InlineRuns = defineComponent({
    name: 'AssistantInlineRuns',
    props: { runs: { type: Array as PropType<InlineRun[]>, required: true } },
    emits: ['navigate'],
    setup (p, { emit: emitRun }) {
      return () => p.runs.map((run, i) => {
        switch (run.kind) {
          case 'bold': { return h('strong', { key: i }, run.text)
          }
          case 'code': { return h('code', { key: i, class: 'ai-md__code' }, run.text)
          }
          case 'route': {
            const to = resolveRouteToken(catalog, run.target)
            if (!to) return h('span', { key: i }, run.label)
            return h('button', {
              'key': i,
              'type': 'button',
              'class': 'ai-link',
              'data-testid': 'ai-route-link',
              'onClick': () => emitRun('navigate', to),
            }, [run.label, h(ArrowUpRight, { 'size': 13, 'class': 'ai-link__icon', 'aria-hidden': 'true' })])
          }
          default: { return run.text
          }
        }
      })
    },
  })
</script>

<style scoped>
  .ai-msg {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    max-width: 100%;
  }

  .ai-msg--user {
    justify-content: flex-end;
  }

  .ai-msg__avatar {
    flex: none;
    width: 26px;
    height: 26px;
    margin-bottom: 4px;
  }

  .ai-msg__bubble {
    min-width: 0;
    max-width: 88%;
    padding: 11px 14px;
    border-radius: 18px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--ai-ink);
  }

  .ai-msg--assistant .ai-msg__bubble {
    border-bottom-left-radius: 6px;
    background: #fff;
    border: 1px solid var(--ai-line);
    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
  }

  .ai-msg--user .ai-msg__bubble {
    border-bottom-right-radius: 6px;
    background: var(--ai-gradient);
    color: #fff;
    box-shadow: 0 10px 24px rgba(79, 70, 229, 0.24);
  }

  .ai-msg__user-text {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .ai-msg__error {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 0;
    color: #b42318;
  }

  .ai-md__h {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 700;
  }

  .ai-md__p {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .ai-md__p + .ai-md__p,
  .ai-md__list + .ai-md__p,
  .ai-md__p + .ai-md__list,
  .ai-md__list + .ai-md__list,
  .ai-md__p + .ai-md__h,
  .ai-md__list + .ai-md__h {
    margin-top: 10px;
  }

  .ai-md__list {
    margin: 0;
    padding-left: 20px;
    display: grid;
    gap: 6px;
  }

  .ai-md__list.is-ordered li::marker {
    color: var(--ai-indigo);
    font-weight: 700;
  }

  .ai-md :deep(.ai-md__code) {
    padding: 1px 6px;
    border-radius: 6px;
    background: var(--ai-surface-2);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
  }

  .ai-md__caret {
    display: inline-block;
    width: 2px;
    height: 1em;
    margin-left: 2px;
    vertical-align: -2px;
    background: var(--ai-indigo);
    animation: ai-blink 1s steps(1) infinite;
  }

  .ai-msg__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed var(--ai-line);
  }

  .ai-msg__sources {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: var(--ai-muted);
    font-size: 12px;
  }

  .ai-msg__source {
    max-width: 180px;
    overflow: hidden;
    padding: 2px 8px;
    border: 1px solid var(--ai-line);
    border-radius: 999px;
    background: var(--ai-surface-2);
    color: var(--ai-muted);
    font: inherit;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    transition: color 140ms, border-color 140ms;
  }

  .ai-msg__source:hover {
    color: var(--ai-indigo);
    border-color: rgba(79, 70, 229, 0.35);
  }

  .ai-msg__rating {
    display: flex;
    gap: 2px;
    flex: none;
  }

  .ai-msg__rate {
    width: 28px;
    height: 28px;
    color: #94a3b8;
  }

  .ai-msg__rate.is-on {
    color: #16a34a;
    background: #e7f8ec;
  }

  .ai-msg__rate.is-down {
    color: #b42318;
    background: #fdecea;
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-md__caret {
      animation: none;
    }
  }
</style>
